import { useCallback, useEffect } from 'react';
import { skipToken } from '@apollo/client';

import useSuspenseQuery from 'commons/useSuspenseQuery';
import useModularPDPVar from 'link-state/modular-pdp/useModularPDPVar';
import getVisitorLocationQueryVariables from 'commons/getVisitorLocationQueryVariables';
import useUserLocation from 'global-state/userLocation/useUserLocation';
import isNewConsumerApp from 'commons/isNewConsumerApp';
import { GET_MODULAR_SOFA_BUNDLE } from './queries';
import { getBundleProductsWithOrientation } from './utils';
import useProductCardOptions from '../useProductCardOptions';

const useModularPDPState = (id, slug, queryOptions = {}) => {
  const { skip = false, skipQuery = true } = queryOptions;
  const userLocation = useUserLocation();

  const skipModularBundleQuery = skip || skipQuery;

  const {
    loading: modularBundleLoading,
    data: modularBundleData,
    error: modularBundleError,
  } = useSuspenseQuery(
    GET_MODULAR_SOFA_BUNDLE,
    isNewConsumerApp && skipModularBundleQuery
      ? skipToken
      : {
          variables: {
            slug,
            zipCode: userLocation?.zip,
            ...getVisitorLocationQueryVariables(userLocation?.stateShortName),
          },
          skip: skipModularBundleQuery,
        }
  );

  const { data: optionsData } = useProductCardOptions(
    modularBundleData?.modularBundle?.modular_parent?.id,
    undefined,
    undefined,
    {
      skip,
    }
  );

  const {
    data: { modularState },
    actions: {
      setModularSlug,
      setModularBundledProducts,
      setModularPreConfigProducts,
      setModularParentOrientationOptions,
      setModularSectionalProduct,
      setModularCollectionTitle,
      updateAddProductSideBarIsOpen,
      updateModularBundleOrderStatus,
      updateModularTotalPrice,
      updateCurrentModularGalleryProduct,
    },
  } = useModularPDPVar();

  const toggleAddProductsSidebar = isOpen => {
    updateAddProductSideBarIsOpen(isOpen);
  };

  const updateModularOrderStatus = useCallback(
    updatedOrder => {
      if (!updatedOrder) {
        return;
      }
      updateModularBundleOrderStatus(updatedOrder);
    },
    [updateModularBundleOrderStatus]
  );

  const updateModularOrderTotal = updatedPrice => {
    updateModularTotalPrice(updatedPrice);
  };

  const updateCurrentGalleryProduct = updatedProduct => {
    if (modularState?.currentModularGalleryProduct?.id === updatedProduct?.id) {
      return;
    }
    updateCurrentModularGalleryProduct(updatedProduct);
  };

  const setSectionalProduct = sectionalProduct => {
    setModularSectionalProduct(sectionalProduct);
    updateCurrentGalleryProduct(sectionalProduct);
  };

  useEffect(() => {
    if (skip) {
      return;
    }

    if (
      modularBundleData?.modularBundle &&
      optionsData?.options &&
      modularState?.modularSlug !==
        modularBundleData?.modularBundle?.modular_parent?.slug
    ) {
      const {
        modularBundle: { modular_parent, products, preConfiguredProducts },
      } = modularBundleData;

      setModularSlug(modular_parent.slug);

      const collectionTitle = modular_parent?.name
        ? modular_parent.name.split(' ')[0]
        : '';

      setModularCollectionTitle(collectionTitle);
      setModularPreConfigProducts(preConfiguredProducts);

      const processedBundleProducts = getBundleProductsWithOrientation({
        bundleProducts: products,
        parentOptions: optionsData.options,
      });

      setModularParentOrientationOptions(
        processedBundleProducts.modularParentOrientationOptionsVal
      );

      setModularBundledProducts(processedBundleProducts.bundleProductsVal);
    }
  }, [
    modularBundleData,
    modularState?.modularSlug,
    optionsData.options,
    setModularBundledProducts,
    setModularCollectionTitle,
    setModularParentOrientationOptions,
    setModularPreConfigProducts,
    setModularSlug,
    skip,
  ]);

  if (skip) {
    return {};
  }

  return {
    loading: modularBundleLoading,
    error: modularBundleError,
    data: {
      modularState,
    },
    actions: {
      toggleAddProductsSidebar,
      updateModularOrderStatus,
      updateModularOrderTotal,
      setModularSectionalProduct: setSectionalProduct,
      updateCurrentGalleryProduct,
    },
  };
};

export default useModularPDPState;
